import LlmsLogo from '@/assets/svg/llms.svg?react';
import { CustomLink } from '@/components/custom-link/CustomLink';
import { useGenerateSpecialFileLink } from '@/hooks/useGenerateSpecialFileLink';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { Dropdown } from 'aui';
import {
  FoxIconAnthropic,
  FoxIconArrowNarrowUpRight,
  FoxIconCheck,
  FoxIconClipboardList,
  FoxIconDownMd,
  FoxIconLoading02,
  FoxIconMarkdownLogo,
  FoxIconOpenAI,
} from 'aui-biz-icons';
import type { ItemType } from 'aui/lib/menu/hooks/useItems';
import clsx from 'clsx';
import React from 'react';
import styles from './SpecialFileMenu.module.css';
import { CopyStatus, useCopyPage } from './hooks/useCopyPage';
export type SpecialFileMenuProps = {
  className?: string;
};

export function SpecialFileMenu(props: SpecialFileMenuProps) {
  const { className } = props;

  const enableCopyPage = useDocsDataModelStore((x) => x.docsBaseConfig.enableCopyPage);
  const enableLLMs = useDocsDataModelStore((x) => x.docsBaseConfig.enableLLMs);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const { markdownSpecialFileLink, llmsSpecialFileLink, chatGPTOpenLink, claudeOpenLink } =
    useGenerateSpecialFileLink();
  const { copyStatus, copyPage } = useCopyPage(markdownSpecialFileLink ?? '');

  const buttonIcon = React.useMemo(() => {
    if (copyStatus === CopyStatus.Fetching) {
      return <FoxIconLoading02 size={12} spin />;
    } else if (copyStatus === CopyStatus.Copied) {
      return <FoxIconCheck size={12} />;
    }
    return <FoxIconClipboardList size={12} />;
  }, [copyStatus]);

  if (!markdownSpecialFileLink || !llmsSpecialFileLink || !enableCopyPage) {
    return null;
  }
  return (
    <Dropdown.Button
      icon={<FoxIconDownMd />}
      className={clsx('flex items-center', className)}
      overlayClassName={styles.overlay}
      onClick={() => {
        copyPage();
      }}
      disabled={copyStatus === CopyStatus.Fetching}
      menu={{
        items: [
          {
            key: 'copyPage',
            icon: (
              <div className="flex h-[22px] items-center">
                <FoxIconClipboardList size={14} />
              </div>
            ),
            label: (
              <div className="text-base font-400 text-color">
                <div className="mb-[1px]">{clientI18n('specialFile.menu.copy.label')}</div>
                <div className="text-sm text-fill-7">
                  {clientI18n('specialFile.menu.copy.description')}
                </div>
              </div>
            ),
            onClick: () => {
              copyPage();
            },
          },
          {
            key: 'markdown',
            icon: (
              <div className="flex h-[22px] items-center">
                <FoxIconMarkdownLogo size={14} />
              </div>
            ),
            label: (
              <CustomLink to={markdownSpecialFileLink} target="_blank">
                <div className="text-base font-400 text-color">
                  <div className="mb-[1px]">
                    {clientI18n('specialFile.menu.markdown.label')}
                    <FoxIconArrowNarrowUpRight size={14} className="ml-1 text-disabled" />
                  </div>
                  <div className="text-sm text-fill-7">
                    {clientI18n('specialFile.menu.markdown.description')}
                  </div>
                </div>
              </CustomLink>
            ),
          },
          chatGPTOpenLink
            ? {
                key: 'chatGPT',
                icon: (
                  <div className="flex h-[22px] items-center">
                    <FoxIconOpenAI size={14} />
                  </div>
                ),
                label: (
                  <CustomLink to={chatGPTOpenLink} target="_blank">
                    <div className="text-base font-400 text-color">
                      <div className="mb-[1px]">
                        {clientI18n('specialFile.menu.gpt.label', {
                          gptName: 'ChatGPT',
                        })}
                        <FoxIconArrowNarrowUpRight size={14} className="ml-1 text-disabled" />
                      </div>
                      <div className="text-sm text-fill-7">
                        {clientI18n('specialFile.menu.gpt.description')}
                      </div>
                    </div>
                  </CustomLink>
                ),
              }
            : undefined,
          claudeOpenLink
            ? {
                key: 'claude',
                icon: (
                  <div className="flex h-[22px] items-center">
                    <FoxIconAnthropic size={14} />
                  </div>
                ),
                label: (
                  <CustomLink to={claudeOpenLink} target="_blank">
                    <div className="text-base font-400 text-color">
                      <div className="mb-[1px]">
                        {clientI18n('specialFile.menu.gpt.label', {
                          gptName: 'Claude',
                        })}
                        <FoxIconArrowNarrowUpRight size={14} className="ml-1 text-disabled" />
                      </div>
                      <div className="text-sm text-fill-7">
                        {clientI18n('specialFile.menu.gpt.description')}
                      </div>
                    </div>
                  </CustomLink>
                ),
              }
            : undefined,
          enableLLMs && {
            key: 'llms',
            icon: (
              <div className="flex h-[22px] items-center">
                <LlmsLogo className="h-4 w-4 rounded" />
              </div>
            ),
            label: (
              <CustomLink to={llmsSpecialFileLink} target="_blank">
                <div className="text-base font-400 text-color">
                  <div className="mb-[1px]">
                    {clientI18n('specialFile.menu.llms.label')}{' '}
                    <FoxIconArrowNarrowUpRight size={14} className="ml-1 text-disabled" />
                  </div>
                  <div className="text-sm text-fill-7">
                    {clientI18n('specialFile.menu.llms.description')}
                  </div>
                </div>
              </CustomLink>
            ),
          },
        ].filter(Boolean) as ItemType[],
      }}
      size="small"
    >
      <span>
        {buttonIcon}
        <span className="ml-1">
          {copyStatus === CopyStatus.Copied
            ? clientI18n('isCopied')
            : clientI18n('specialFile.menu.copy.label')}
        </span>
      </span>
    </Dropdown.Button>
  );
}
